// src/components/SoftGate.jsx
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

function SoftUpgradeCard({ title, subtitle, ctaTo, ctaLabel }) {
  return (
    <div style={{ padding: 24, maxWidth: 880, margin: "0 auto" }}>
      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 14,
          padding: 18,
          background: "#fff",
          boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>
          {title}
        </div>
        <div style={{ color: "#6b7280", lineHeight: 1.5, marginBottom: 14 }}>
          {subtitle}
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Link
            to={ctaTo}
            style={{
              display: "inline-block",
              padding: "10px 14px",
              borderRadius: 10,
              background: "#111827",
              color: "#fff",
              textDecoration: "none",
              fontWeight: 600,
            }}
          >
            {ctaLabel}
          </Link>

          <Link
            to="/plans/compare-plans"
            style={{
              display: "inline-block",
              padding: "10px 14px",
              borderRadius: 10,
              border: "1px solid #e5e7eb",
              color: "#111827",
              textDecoration: "none",
              fontWeight: 600,
              background: "#fff",
            }}
          >
            Comparer les plans
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * SoftGate
 * - rule: "ai_view" | "ai_run" | "premium_or_more" | "advisor_premium" etc.
 * - mode: "view" (autorise lecture) ou "run" (autorise actions)
 */
export default function SoftGate({
  children,
  rule = "premium_or_more",
  title,
  subtitle,
}) {
  const { profile, subscription } = useAuth();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const check = useMemo(() => {
    const role = String(profile?.role || "").toLowerCase();
    const plan = String(profile?.plan || subscription?.plan || "").toLowerCase();

    const isAdminOrTeam = role === "admin" || role === "team";
    const isFO = role === "family_office";
    const isExpert = role === "expert";

    const isPremium = plan === "premium";
    const isAdvisor = plan === "art_advisor";
    const isAdvisorPremium = plan === "art_advisor_premium";
    const isFamilyOfficePlan = plan === "family_office";

    // Helpers
    const premiumOrMore =
      isPremium || isAdvisor || isAdvisorPremium || isFamilyOfficePlan || isFO || isAdminOrTeam;

    const canRunAI =
      isPremium || isAdvisorPremium || isFamilyOfficePlan || isFO || isAdminOrTeam;

    const canViewAI =
      premiumOrMore || isExpert; // experts voient parfois des résultats (selon ton modèle)

    switch (rule) {
      case "ai_view":
        return { ok: canViewAI };
      case "ai_run":
        return { ok: canRunAI };
      case "premium_or_more":
      default:
        return { ok: premiumOrMore };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.role, profile?.plan, subscription?.plan]);

  if (check.ok) return children;

  const fallbackTitle =
    title || "Cette fonctionnalité est disponible avec Premium (ou un plan Pro).";
  const fallbackSubtitle =
    subtitle ||
    "Tu peux explorer l’interface, mais l’activation des analyses et modules avancés nécessite une mise à niveau.";

  return (
    <SoftUpgradeCard
      title={fallbackTitle}
      subtitle={fallbackSubtitle}
      ctaTo="/pricing"
      ctaLabel="Activer (Upgrade)"
    />
  );
}